<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { PlusIcon } from '@heroicons/vue/24/outline'
import {
  CONSOLIDATED_AREA_METRICS,
  consolidatedMetricMeta,
  type IConsolidatedReport,
} from '@/interfaces/entities/management'
import { consolidatedKey, useManagementStore } from '@/store/management'

const store = useManagementStore()

const now = new Date()
const yearOptions = computed(() => {
  const current = now.getFullYear()
  return [current, current - 1, current - 2, current - 3]
})

const columns = computed(() => CONSOLIDATED_AREA_METRICS.map((key) => consolidatedMetricMeta(key)).filter(Boolean))

// Meses do ano selecionado (até o mês corrente, se for o ano atual).
const months = computed(() => {
  const year = store.consolidatedYear
  const lastMonth = year < now.getFullYear() ? 12 : now.getMonth() + 1
  return Array.from({ length: lastMonth }, (_, i) => {
    const m = i + 1
    return {
      key: `${year}-${String(m).padStart(2, '0')}`,
      label: new Date(year, i, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', ''),
      isCurrent: year === now.getFullYear() && m === now.getMonth() + 1,
    }
  })
})

// Áreas adicionadas (filtra as que ainda existem na árvore).
const areas = computed(() =>
  store.consolidatedAreaIds
    .map((id) => store.nodesById[id])
    .filter((area): area is NonNullable<typeof area> => !!area),
)

const activeArea = computed(() => store.activeArea)
const canAdd = computed(() => !!activeArea.value && !store.consolidatedAreaIds.includes(activeArea.value.id))

const loadingKeys = ref<Set<string>>(new Set())

function reportFor(areaId: number): IConsolidatedReport {
  return {
    id: `consolidada-${areaId}`,
    areaId,
    areaLabel: store.nodesById[areaId]?.name ?? 'Área',
    sensorId: null,
    sensorLabel: null,
    monthsBack: 12,
    metrics: CONSOLIDATED_AREA_METRICS,
  }
}

function cellValues(areaId: number, monthKey: string): Record<string, number | null> | null {
  const key = consolidatedKey(areaId, null, CONSOLIDATED_AREA_METRICS, monthKey)
  return store.consolidatedRows[key]?.values ?? null
}

function rowsFor(areaId: number) {
  return months.value.map((m) => {
    const key = consolidatedKey(areaId, null, CONSOLIDATED_AREA_METRICS, m.key)
    return {
      ...m,
      values: store.consolidatedRows[key]?.values ?? null,
      complete: store.consolidatedRows[key]?.complete ?? false,
      loading: loadingKeys.value.has(key),
    }
  })
}

function totalsFor(areaId: number): Record<string, number | null> {
  const result: Record<string, number | null> = {}
  columns.value.forEach((col) => {
    if (!col) return
    const vals = months.value
      .map((m) => cellValues(areaId, m.key)?.[col.key])
      .filter((v): v is number => typeof v === 'number')
    if (!vals.length) {
      result[col.key] = null
      return
    }
    const sum = vals.reduce((a, b) => a + b, 0)
    result[col.key] = col.agg === 'avg' ? sum / vals.length : sum
  })
  return result
}

async function fetchMonth(areaId: number, monthKey: string, refresh: boolean) {
  const key = consolidatedKey(areaId, null, CONSOLIDATED_AREA_METRICS, monthKey)
  if (loadingKeys.value.has(key)) return
  loadingKeys.value = new Set(loadingKeys.value).add(key)
  try {
    await store.fetchReportMonth(reportFor(areaId), monthKey, refresh)
  } finally {
    const next = new Set(loadingKeys.value)
    next.delete(key)
    loadingKeys.value = next
  }
}

async function loadArea(areaId: number, force = false) {
  for (const m of months.value) {
    const needFetch = !cellValues(areaId, m.key) || (force && m.isCurrent)
    if (!needFetch) continue
    // eslint-disable-next-line no-await-in-loop
    await fetchMonth(areaId, m.key, force && m.isCurrent)
  }
}

function addArea() {
  if (!activeArea.value) return
  store.addConsolidatedArea(activeArea.value.id)
}

// Formatação por métrica (utilização %, tempo em h/min, consumo kWh, contagens inteiras).
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins ? `${hours}h ${mins}min` : `${hours}h`
}

function formatCell(key: string, value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (key === 'utilizacao') return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
  if (key === 'tempo_parada') return formatDuration(value)
  if (key === 'consumo_total') return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} kWh`
  return value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
}

// Carrega os meses ao adicionar áreas ou trocar o ano.
watch(
  () => [areas.value.map((a) => a.id).join(','), store.consolidatedYear] as const,
  () => areas.value.forEach((area) => loadArea(area.id)),
  { immediate: true },
)
</script>

<template>
  <div class="space-y-4">
    <!-- Barra: adicionar área + ano -->
    <div class="flex flex-wrap items-center justify-between gap-3 border border-stroke bg-white px-4 py-3">
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="flex items-center gap-1 border border-primary px-3 py-2 text-sm font-medium text-primary transition hover:bg-primary/10 disabled:opacity-40"
          :disabled="!canAdd"
          :title="activeArea ? 'Adiciona a área selecionada à comparação' : 'Selecione uma área nas colunas acima'"
          @click="addArea">
          <PlusIcon class="h-4 w-4" />
          {{ activeArea ? `Adicionar “${activeArea.name}”` : 'Selecione uma área' }}
        </button>
        <span class="text-xs text-slate-400">Selecione uma área nas colunas e adicione para comparar.</span>
      </div>
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-slate-600">Ano:</label>
        <select
          :value="store.consolidatedYear"
          class="border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
          @change="store.setConsolidatedYear(Number(($event.target as HTMLSelectElement).value))">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>
    </div>

    <div v-if="!areas.length" class="border border-stroke bg-slate-50 p-8 text-center text-sm text-slate-400">
      Nenhuma área adicionada. Selecione uma linha de produção nas colunas e clique em “Adicionar”.
    </div>

    <!-- Uma tabela mensal por área (comparáveis lado a lado) -->
    <div v-for="area in areas" :key="area.id" class="border border-stroke bg-white">
      <header class="flex items-center justify-between gap-2 border-b border-stroke bg-slate-50 px-4 py-2.5">
        <div class="min-w-0">
          <h3 class="truncate font-semibold text-slate-800">{{ area.name }}</h3>
          <p class="text-xs text-slate-400">Consolidado mensal de {{ store.consolidatedYear }}</p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            class="border border-stroke px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            title="Recalcula o mês corrente"
            @click="loadArea(area.id, true)">
            ↻
          </button>
          <button
            type="button"
            class="border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red hover:bg-red-50"
            @click="store.removeConsolidatedArea(area.id)">
            Remover
          </button>
        </div>
      </header>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead class="bg-slate-50/60 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th class="px-4 py-2.5">Mês</th>
              <th v-for="col in columns" :key="col!.key" class="px-4 py-2.5 text-right">{{ col!.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rowsFor(area.id)" :key="row.key" class="border-t border-stroke">
              <td class="px-4 py-2.5 font-medium capitalize text-slate-700">
                {{ row.label }}
                <span v-if="row.values && !row.complete" class="ml-1 text-[10px] text-amber-500">(em curso)</span>
              </td>
              <template v-if="row.loading">
                <td :colspan="columns.length" class="px-4 py-2.5 text-center text-xs text-slate-400">calculando…</td>
              </template>
              <template v-else-if="row.values">
                <td
                  v-for="col in columns"
                  :key="col!.key"
                  class="px-4 py-2.5 text-right"
                  :class="col!.key === 'utilizacao' ? 'font-bold text-slate-900' : 'text-slate-800'">
                  {{ formatCell(col!.key, row.values[col!.key]) }}
                </td>
              </template>
              <template v-else>
                <td :colspan="columns.length" class="px-4 py-2.5 text-center text-xs text-slate-400">—</td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-primary bg-primary/5">
              <td class="px-4 py-3 text-xs font-bold uppercase text-primary">Total / média</td>
              <td
                v-for="col in columns"
                :key="col!.key"
                class="px-4 py-3 text-right font-extrabold text-slate-900">
                {{ formatCell(col!.key, totalsFor(area.id)[col!.key]) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>
